// Home.jsx
import React from 'react';
import { useLocation, useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../resource/defaults.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { prepareListPageMeta } from '../resource/prepareListPageMeta.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';
import { getHomePageConfig } from '../resource/homeUtils.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('Home loader()');
  const errorLog = legacySSRError.extend('Home loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    const state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData });

    // Get config from pathname
    const url = new URL(request.url);
    const pageConfig = getHomePageConfig({ pathname: url.pathname });

    const {
      paths: [canonicalPath],
      titleKey,
      descriptionKey,
      keywordsKey,
      jsonLdType,
      robots = [],
      shouldAppendBrandingSuffix,
    } = pageConfig;

    const availableLanguages = getRemoteConfigData(state, SUPPORTED_LANGUAGES);
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareListPageMeta({
      state,
      titleKey,
      descriptionKey,
      keywordsKey,
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      jsonLdType,
      robots,
      shouldAppendBrandingSuffix,
      i18n: i18nInstance,
    });

    log('meta data prepared', { meta });

    return {
      data: { store: {} },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const HomeRoute = () => {
  const { pathname } = useLocation();
  const pageConfig = getHomePageConfig({ pathname });
  const shouldEnableSentrySpan = !pageConfig?.shouldDisableSentrySpan;

  const { data, error, meta = EMPTY_ARRAY } = useLoaderData() || EMPTY_OBJECT;

  useSSRDataMerge({
    componentName: 'Home',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalComponent
        filename="Container_Home"
        shouldEnableSentrySpan={shouldEnableSentrySpan}
        importer={/* #__LOADABLE__ */ () => import('../container/Home.js')}
      />
    </>
  );
};

export default HomeRoute;
